import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { 
  auth, 
  googleProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  onAuthStateChanged,
  signInWithPopup
} from "@/lib/firebase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Get the ID token to send to backend
        const token = await firebaseUser.getIdToken();
        
        // Sync user with backend
        try {
          const response = await fetch(`${API}/auth/firebase/sync`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            credentials: 'include',
            body: JSON.stringify({
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              name: firebaseUser.displayName,
              picture: firebaseUser.photoURL,
              email_verified: firebaseUser.emailVerified
            })
          });
          
          if (response.ok) {
            const userData = await response.json();
            setUser({
              ...userData,
              firebaseUser: firebaseUser,
              emailVerified: firebaseUser.emailVerified
            });
          }
        } catch (error) {
          // Backend sync non-critical - still set user from Firebase
          setUser({
            email: firebaseUser.email,
            name: firebaseUser.displayName,
            picture: firebaseUser.photoURL,
            emailVerified: firebaseUser.emailVerified,
            firebaseUser: firebaseUser
          });
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginWithGoogle = async () => {
    setAuthError(null);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      return { success: true, user: result.user };
    } catch (error) {
      let message = "Error al iniciar sesión con Google";
      if (error.code === 'auth/popup-closed-by-user') {
        message = "Ventana cerrada. Intenta de nuevo.";
      } else if (error.code === 'auth/popup-blocked') {
        message = "Ventana bloqueada. Permite pop-ups e intenta de nuevo.";
      }
      setAuthError(message);
      return { success: false, error: message };
    }
  };

  const loginWithEmail = async (email, password) => {
    setAuthError(null);
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      if (!result.user.emailVerified) {
        return { 
          success: false, 
          error: "Por favor verifica tu correo antes de iniciar sesión. Revisa tu bandeja de entrada.",
          needsVerification: true 
        };
      }
      
      return { success: true, user: result.user };
    } catch (error) {
      let message = "Error al iniciar sesión";
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        message = "Email o contraseña incorrectos";
      } else if (error.code === 'auth/too-many-requests') {
        message = "Demasiados intentos. Intenta más tarde.";
      } else if (error.code === 'auth/user-disabled') {
        message = "Esta cuenta ha sido deshabilitada.";
      }
      setAuthError(message);
      return { success: false, error: message };
    }
  };

  const registerWithEmail = async (email, password, name, phone) => {
    setAuthError(null);
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Send verification email
      await sendEmailVerification(result.user);
      
      // Save additional user data to backend
      const token = await result.user.getIdToken();
      await fetch(`${API}/auth/firebase/sync`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          uid: result.user.uid,
          email: result.user.email,
          name: name || email.split('@')[0],
          phone: phone,
          email_verified: false
        })
      });
      
      // Sign out because email is not verified yet
      await signOut(auth);
      
      return { 
        success: true, 
        message: "¡Cuenta creada! Revisa tu correo para verificar tu cuenta.",
        needsVerification: true
      };
    } catch (error) {
      let message = "Error al crear la cuenta";
      if (error.code === 'auth/email-already-in-use') {
        message = "Este email ya está registrado";
      } else if (error.code === 'auth/weak-password') {
        message = "La contraseña debe tener al menos 6 caracteres";
      } else if (error.code === 'auth/invalid-email') {
        message = "Email inválido";
      }
      setAuthError(message);
      return { success: false, error: message };
    }
  };

  const resetPassword = async (email) => {
    setAuthError(null);
    try {
      await sendPasswordResetEmail(auth, email);
      return { 
        success: true, 
        message: "Se ha enviado un enlace para restablecer tu contraseña" 
      };
    } catch (error) {
      let message = "Error al enviar el correo";
      if (error.code === 'auth/user-not-found') {
        message = "No existe una cuenta con este email";
      }
      setAuthError(message);
      return { success: false, error: message };
    }
  };

  const resendVerificationEmail = async () => {
    try {
      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
        return { success: true, message: "Email de verificación enviado" };
      }
      return { success: false, error: "No hay usuario activo" };
    } catch (error) {
      return { success: false, error: "Error al enviar el email" };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      // Also logout from backend
      await fetch(`${API}/auth/logout`, { 
        method: 'POST', 
        credentials: 'include' 
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const clearAuthError = () => {
    setAuthError(null);
  };

  // Check auth state - returns a promise that resolves when auth state is determined
  const checkAuth = useCallback(async () => {
    if (!loading) {
      return user;
    }
    // Wait for auth state to be determined
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (!loading) {
          clearInterval(checkInterval);
          resolve(user);
        }
      }, 100);
    });
  }, [loading, user]);

  // Legacy login function for compatibility
  const login = loginWithGoogle;

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      loginWithGoogle,
      loginWithEmail,
      registerWithEmail,
      resetPassword,
      resendVerificationEmail,
      logout, 
      updateUser, 
      authError,
      clearAuthError,
      checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
